// Small readers for untrusted JSON (game data and saved games). Each one checks a value and
// returns it typed, or throws a DataError naming the path of the offending value.
import type { Cell, Side } from './geometry';

export class DataError extends Error {
  override name = 'DataError';
}

export function fail(path: string, problem: string): never {
  throw new DataError(`${path}: ${problem}`);
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function record(value: unknown, path: string): Record<string, unknown> {
  if (!isRecord(value)) fail(path, 'debe ser un objeto');
  return value;
}

export function list(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) fail(path, 'debe ser una lista');
  return value;
}

export function text(value: unknown, path: string): string {
  if (typeof value !== 'string' || value === '') fail(path, 'debe ser un texto');
  return value;
}

export function num(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) fail(path, 'debe ser un número');
  return value;
}

export function positive(value: unknown, path: string): number {
  const n = num(value, path);
  if (n <= 0) fail(path, 'debe ser mayor que cero');
  return n;
}

export function nonNegative(value: unknown, path: string): number {
  const n = num(value, path);
  if (n < 0) fail(path, 'no puede ser negativo');
  return n;
}

export function int(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isInteger(value)) fail(path, 'debe ser un entero');
  return value;
}

export function bool(value: unknown, path: string): boolean {
  if (typeof value !== 'boolean') fail(path, 'debe ser verdadero o falso');
  return value;
}

export function cell(value: unknown, path: string): Cell {
  const arr = list(value, path);
  if (arr.length !== 2) fail(path, 'debe tener columna y fila');
  return [int(arr[0], `${path}[0]`), int(arr[1], `${path}[1]`)];
}

export function side(value: unknown, path: string): Side {
  if (value !== 'N' && value !== 'E' && value !== 'S' && value !== 'W') fail(path, 'debe ser N, E, S u O (W)');
  return value;
}

export function oneOf<T extends string>(value: unknown, options: readonly T[], path: string): T {
  if (typeof value !== 'string' || !(options as readonly string[]).includes(value)) fail(path, `debe ser uno de ${options.join(', ')}`);
  return value as T;
}

/** Looks up `id` in a table of known definitions, failing with the path if it is unknown. */
export function ref<T>(table: ReadonlyMap<string, T>, value: unknown, path: string): T {
  const id = text(value, path);
  const found = table.get(id);
  if (found === undefined) fail(path, `«${id}» no existe`);
  return found;
}
